import {
  DownloadCloud01,
  Eye,
  File04,
  Trash03,
  CheckCircle,
} from "@untitled-ui/icons-react";

import {
  formatBytes,
  useFileUpload,
  FileMetadata,
} from "../../hooks/use-file-upload";
import { Button } from "../ui/button";
import { useEffect } from "react";
import { AlertCircleIcon } from "lucide-react";

interface UploadOneFileProps {
  onFileChange?: (file: File | null) => void;
  onShowFile?: () => void;
  subText?: string;
  hidePreviewButton?: boolean;
  initialFile?: FileMetadata;
  removeFileProps?: () => void;
}

export const UploadOneFile = ({ removeFileProps, onFileChange, onShowFile, subText, hidePreviewButton = false, initialFile }: UploadOneFileProps) => {
  const maxSize = 10 * 1024 * 1024; // 10MB
  const [
    { files, isDragging, errors },
    {
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      openFileDialog,
      removeFile,
      getInputProps,
    },
  ] = useFileUpload({ maxSize, initialFiles: initialFile ? [initialFile] : [] });

const file = files[0];
   useEffect(() => {
    onFileChange?.(file?.file instanceof File ? file.file : null);
  }, [file]);

  return (
    <div className="flex flex-col gap-2">
      {!file && (
        <div
          role="button"
          onClick={openFileDialog}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          data-dragging={isDragging || undefined}
          className="border-input hover:bg-accent/50 data-[dragging=true]:bg-accent/50 has-[input:focus]:border-ring has-[input:focus]:ring-ring/50 flex min-h-40 flex-col items-center justify-center rounded-xl border border-dashed p-6 transition-colors cursor-pointer"
        >
          <input
            {...getInputProps()}
            className="inset-0 w-full h-full opacity-0 cursor-pointer"
            aria-label="Upload file"
          />

          <div>
            <span className="inline-block bg-gray-100 p-3 rounded-full">
              <DownloadCloud01 className="w-6 h-6" />
            </span>
          </div>
          <span className="text-base mt-2 text-center">
            <span className="text-text-blue font-semibold">Cliquez pour ajouter</span>{" "}
            ou drag and drop
            {" "}
            {subText ? (
              <span>{subText}</span>
            ) : (
              <span>(max. {formatBytes(maxSize)})</span>
            )}
          </span>
        </div>
      )}

      {errors.length > 0 && (
        <div className="text-destructive flex items-center gap-1 text-sm" role="alert">
          <AlertCircleIcon className="size-4 shrink-0" />
          <span>{errors[0]}</span>
        </div>
      )}

      {file && (
        <div className="relative w-full flex flex-col justify-between gap-4 border border-dashed rounded-xl p-6 bg-white">
          <div className="flex justify-between items-center">
            <div className="flex items-start gap-4 text-base text-gray-800">
              <File04 className="w-6 h-6" />
              <div className="text-left">
                <div className="font-semibold text-md">{file.file.name}</div>
                <div className="text-sm text-gray-500">{formatBytes(file.file.size)}</div>
              </div>
            </div>

            <div className="flex gap-3">
            {!hidePreviewButton && (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => {
                  if (onShowFile) {
                    onShowFile();
                  } else {
                    const url = file.file instanceof File ? URL.createObjectURL(file.file) : file.file.url;
                    window.open(url, "_blank");
                  }
                }}
                className="h-10 w-10 text-muted-foreground hover:text-foreground rounded-lg ring-1 ring-input hover:bg-accent"
              >
                <Eye className="size-6" />
              </Button>
            )}

              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => {
                  if (file.id) {
                    removeFile(file.id);
                    removeFileProps?.();
                  }
                }}
                className="h-10 w-10 text-muted-foreground hover:text-destructive rounded-lg ring-1 ring-input hover:bg-accent"
              >
                <Trash03 className="size-6" />
              </Button>
            </div>
          </div>

          <div className="flex justify-end">
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadOneFile;
